import { useCallback, useEffect, useRef, useState } from 'react';
import type { ActionIA, PropositionIA, TourAssistant } from '@camellie/shared';
import { MAX_TOURS } from '@camellie/shared';
import { api, ErreurApi } from '../api/client';
import { Dictee, dicteePossible } from '../voix/dictee';
import { Enregistreur, enregistrementPossible, transcrire } from '../voix/enregistreur';
import { amorcerParole, dire, parolePossible, taire } from '../voix/parole';

interface Props {
  /**
   * Cree tout un lot et rend de quoi l'annuler.
   *
   * L'annulation revient ici parce qu'en conversation la feuille ne se ferme
   * pas : le bandeau de l'app, qui vit sous le voile, serait invisible. On
   * annule donc sur le tour de parole concerne, la ou on l'a entendu.
   */
  onAjouter: (propositions: PropositionIA[]) => () => void;
  /** Gestes sur des elements existants : cocher, deplacer, supprimer. */
  onAgir: (actions: ActionIA[]) => () => void;
  onFermer: () => void;
}

/** Un tour affiche. `annuler` n'existe que sur les tours qui ont ajoute. */
interface Tour {
  role: 'moi' | 'assistant';
  texte: string;
  annuler?: () => void;
  annule?: boolean;
}

const EXEMPLES = [
  'J’ai un rendez-vous à la clinique mardi à 5h',
  'Il faut du lait et des œufs',
  'Quand est mon rendez-vous chez le dentiste ?',
];

/**
 * L'assistant : on lui parle, il repond a voix haute, et la conversation
 * continue.
 *
 * **On maintient le gros bouton pendant qu'on parle, on relache quand on a
 * fini** — le talkie-walkie, pas le mains libres.
 *
 * Le mains libres a ete essaye et abandonne. Il reposait sur la detection d'un
 * blanc pour decider que la phrase etait finie, et ce reglage n'a jamais
 * marche pour tout le monde : trop court, il coupait la parole a qui cherche
 * ses mots ; trop long, il faisait attendre. Le bouton supprime la question —
 * celui qui parle sait seul quand il a fini, et il le dit en relachant.
 *
 * Il supprime du meme coup l'echo (le micro n'ecoute jamais pendant que
 * l'assistant parle) et l'ambiguite (on voit ce qu'on tient).
 *
 * Trois regles de conduite gouvernent le reste du fichier :
 *
 *  1. **Le moteur ne se ferme jamais** tant qu'on est en mode vocal : il
 *     tourne sourd entre les tours, et l'appui ne fait que lui rendre
 *     l'oreille. Le fermer obligerait a le redemarrer, et ce demarrage — des
 *     centaines de millisecondes sur iOS — emportait les premiers mots.
 *  2. **L'assistant dit toujours quelque chose.** Meme une panne reseau se
 *     dit : un silence ne se distingue pas d'un micro ferme, et l'on repete sa
 *     phrase, ce qui cree tout en double.
 *  3. **Le fil est conserve** et renvoye au serveur, sans quoi « et a quelle
 *     heure ? » ne serait qu'une phrase sans sujet.
 *
 * L'ecrit reste possible pour qui est dans une salle d'attente, et garde son
 * comportement d'origine : une phrase, un ajout, la feuille se ferme.
 */
export function Assistant({ onAjouter, onAgir, onFermer }: Props) {
  const [tours, setTours] = useState<Tour[]>([]);
  const [texte, setTexte] = useState('');
  const [partiel, setPartiel] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [ecoute, setEcoute] = useState(false);
  const [parle, setParle] = useState(false);
  const [vocal, setVocal] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  /**
   * Le fil, en double : l'etat pour l'affichage, la reference pour les
   * rappels. La dictee et la synthese rappellent longtemps apres le rendu qui
   * les a installes ; sans reference, elles liraient un fil fige au premier
   * tour.
   */
  const filRef = useRef<Tour[]>([]);
  const vocalRef = useRef(false);
  const dictee = useRef<Dictee | null>(null);
  const enregistreur = useRef<Enregistreur | null>(null);
  const bas = useRef<HTMLDivElement | null>(null);

  const [vocalPossible] = useState(
    () => (enregistrementPossible() || dicteePossible()) && parolePossible(),
  );

  const ajouterTour = useCallback((tour: Tour) => {
    filRef.current = [...filRef.current, tour];
    setTours(filRef.current);
  }, []);

  // Toujours montrer le dernier tour : en conversation, la reponse arrive
  // pendant qu'on regarde ailleurs.
  useEffect(() => {
    bas.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [tours, partiel, occupe]);

  const arreterVocal = useCallback(() => {
    vocalRef.current = false;
    setVocal(false);
    dictee.current?.arreter();
    // Rend le micro au systeme : sans cela l indicateur d enregistrement
    // reste allume sur le telephone, ce qui inquiete a juste titre.
    enregistreur.current?.liberer();
    enregistreur.current = null;
    taire();
    setEcoute(false);
    setParle(false);
    setPartiel('');
  }, []);

  const fermer = useCallback(() => {
    arreterVocal();
    onFermer();
  }, [arreterVocal, onFermer]);

  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fermer();
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [fermer]);

  /**
   * Couper le micro et la voix — **au demontage seulement**.
   *
   * Cet effet est separe du precedent, et ses dependances sont vides, pour une
   * raison qui a coute cher : le parent recree `onFermer` a chaque rendu, donc
   * `fermer` change, donc un effet qui en depend se nettoie. Le nettoyage
   * liberait le micro et remettait `vocalRef` a false — et l'app se rend a
   * nouveau precisement quand l'assistant vient de creer une tache, pour
   * afficher le bandeau « Annuler ».
   *
   * Resultat : la conversation mourait juste apres avoir servi, et il fallait
   * fermer puis rouvrir l'assistant. Un nettoyage de demontage ne doit jamais
   * dependre d'une fonction venue du parent.
   */
  useEffect(() => {
    return () => {
      vocalRef.current = false;
      dictee.current?.arreter();
      enregistreur.current?.liberer();
      enregistreur.current = null;
      taire();
    };
  }, []);

  /**
   * L'appui sur le gros bouton : on ecoute tant qu'il est tenu.
   *
   * Le moteur tourne deja — il a ete demarre a l'entree en mode vocal et n'est
   * jamais ferme depuis. Appuyer ne fait que lui rendre l'oreille, donc il n'y
   * a aucun demarrage a attendre : les premiers mots sont captes.
   */
  const commencerAParler = useCallback(() => {
    if (!vocalRef.current) return;
    navigator.vibrate?.(8);
    setErreur(null);
    if (enregistreur.current) {
      enregistreur.current.demarrer();
      setEcoute(true);
      return;
    }
    dictee.current?.rendreAudible();
  }, []);

  /** Dit une phrase, puis rend la parole a l'utilisateur. */
  const direEtRendreLaParole = useCallback(
    async (phrase: string) => {
      if (!vocalRef.current) return;
      setParle(true);
      try {
        await dire(phrase);
      } finally {
        setParle(false);
      }
      // Le micro reste sourd : c'est l'appui sur le bouton qui rend l'oreille.
      // Rien ne s'enregistre tant que personne n'a demande a parler.
    },
    [],
  );

  const envoyer = useCallback(
    async (dit: string, enVocal: boolean) => {
      const propre = dit.trim();
      if (propre === '') return;

      // On cesse d'ecouter pendant la reflexion et la reponse — sans fermer le
      // micro. Ce qu'on dirait entre-temps se melangerait au tour suivant, et
      // la voix de synthese serait transcrite ; mais un micro qu'on ferme est
      // un micro qu'il faut rallumer, et ce demarrage coutait les premiers
      // mots de la phrase d'apres.
      dictee.current?.rendreSourd();
      setPartiel('');
      setErreur(null);

      // Le fil est preleve avant d'y ajouter la phrase du moment, qui part
      // dans `texte` et n'a pas a y figurer deux fois.
      const historique: TourAssistant[] = filRef.current
        .slice(-MAX_TOURS)
        .map((t) => ({ role: t.role, texte: t.texte }));

      ajouterTour({ role: 'moi', texte: propre });
      setTexte('');
      setOccupe(true);

      try {
        const res = await api.assistant(propre, historique);

        // Un tour ne produit jamais les deux : le serveur tranche en faveur
        // d'« agir » quand le modele a hesite, pour ne pas creer un doublon de
        // ce qu'on lui demandait de modifier.
        let annuler: (() => void) | undefined;
        if (res.actions.length > 0) annuler = onAgir(res.actions);
        else if (res.propositions.length > 0) annuler = onAjouter(res.propositions);

        // A l'ecrit, un ajout ferme la feuille comme avant : dire « il faut du
        // lait » doit couter une phrase, pas une phrase plus deux tapes. A la
        // voix il n'y a rien a fermer, et la conversation continue. Un geste
        // sur l'existant garde la feuille ouverte dans les deux cas : on
        // enchaine presque toujours (« et celui de jeudi aussi »).
        if (!enVocal && res.mode === 'ajout' && res.propositions.length > 0) {
          fermer();
          return;
        }

        ajouterTour({ role: 'assistant', texte: res.reponse, annuler });
        await direEtRendreLaParole(res.reponse);
      } catch (e) {
        const message = e instanceof ErreurApi ? e.message : 'L’assistant n’a pas répondu.';
        // La panne se dit, elle aussi : a la voix, une erreur muette est
        // indiscernable d'un micro qui n'a rien entendu.
        ajouterTour({ role: 'assistant', texte: message });
        await direEtRendreLaParole(message);
      } finally {
        setOccupe(false);
      }
    },
    [ajouterTour, direEtRendreLaParole, fermer, onAgir, onAjouter],
  );

  /** Ouverte au premier besoin : instancier un moteur vocal a vide coute une permission. */
  const preparerDictee = useCallback(() => {
    if (dictee.current) return dictee.current;
    dictee.current = new Dictee({
      // Le decoupage au silence est desactive : c'est le relachement du bouton
      // qui clot le tour. Personne ne devine la fin d'une phrase aussi bien
      // que celui qui la prononce.
      onPhrase: () => undefined,
      onPartiel: setPartiel,
      onEcoute: setEcoute,
      onErreur: (message) => {
        setErreur(message);
        arreterVocal();
      },
    });
    return dictee.current;
  }, [arreterVocal]);

  /**
   * Relachement du bouton : le tour est fini, on envoie ce qui a ete entendu.
   */
  const finirDeParler = useCallback(() => {
    if (!vocalRef.current) return;

    // Chemin serveur : on a enregistre, il faut maintenant transcrire. La
    // phrase n'existe pas encore, d'ou l'attente affichee — c'est le seul
    // moment ou ce chemin coute plus cher que le moteur du navigateur.
    const enr = enregistreur.current;
    if (enr) {
      setEcoute(false);
      setOccupe(true);
      void (async () => {
        try {
          const audio = await enr.arreter();
          if (!audio || audio.size < 1200) {
            setErreur('Je n’ai rien entendu. Maintiens le bouton pendant que tu parles.');
            return;
          }
          const phrase = await transcrire(audio);
          if (phrase === '') {
            setErreur('Je n’ai rien compris. Réessaie en parlant normalement.');
            return;
          }
          await envoyer(phrase, true);
        } catch {
          setErreur('La transcription n’a pas répondu. Réessaie.');
        } finally {
          setOccupe(false);
        }
      })();
      return;
    }

    const phrase = dictee.current?.cloreTour() ?? '';
    if (phrase === '') {
      setErreur('Je n’ai rien entendu. Maintiens le bouton pendant que tu parles.');
      return;
    }
    void envoyer(phrase, true);
  }, [envoyer]);

  function entrerEnVocal() {
    if (vocalRef.current) {
      arreterVocal();
      return;
    }
    // Les deux amorces doivent partir du geste lui-meme : la synthese comme le
    // micro exigent une interaction, et un appel differe ne compte pas.
    amorcerParole();
    setErreur(null);
    vocalRef.current = true;
    setVocal(true);

    // On prefere la transcription serveur : meme resultat sur tous les
    // appareils, et le francais du Quebec est annonce au modele. Le moteur du
    // navigateur ne sert plus que la ou l'enregistrement est impossible.
    if (enregistrementPossible()) {
      const enr = new Enregistreur();
      enr
        .preparer()
        .then(() => {
          enregistreur.current = enr;
        })
        .catch(() => {
          // Micro refuse ou indisponible : on retombe sur le navigateur, qui
          // demandera lui-meme la permission.
          enregistreur.current = null;
          const d = preparerDictee();
          d.demarrer();
          d.rendreSourd();
        });
      return;
    }

    // Le moteur demarre ici et ne se fermera plus : il chauffe pendant qu'on
    // lit l'ecran, et sera pret des le premier appui.
    const d = preparerDictee();
    d.demarrer();
    d.rendreSourd();
  }

  function annulerTour(indice: number) {
    const tour = filRef.current[indice];
    if (!tour?.annuler || tour.annule) return;
    tour.annuler();
    filRef.current = filRef.current.map((t, i) => (i === indice ? { ...t, annule: true } : t));
    setTours(filRef.current);
  }

  const etat = parle
    ? 'Je parle…'
    : occupe
      ? 'Je réfléchis…'
      : ecoute
        ? 'J’écoute…'
        : 'Maintiens pour parler';

  return (
    <div className="voile" onClick={fermer} role="presentation">
      <div
        className={'feuille feuille-conversation' + (vocal ? ' en-conversation' : '')}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="feuille-poignee" aria-hidden="true" />
        <h2 className="feuille-titre">Assistant</h2>

        <div className="fil">
          {tours.length === 0 && !vocal && (
            <div className="fil-vide">
              <p className="note">
                {vocalPossible
                  ? 'Appuie sur le micro, puis MAINTIENS le gros bouton pendant que tu parles. Relâche quand tu as fini.'
                  : 'Écris-moi — ou utilise la touche micro du clavier.'}
              </p>
              <div className="exemples">
                {EXEMPLES.map((ex) => (
                  <button key={ex} type="button" className="exemple" onClick={() => setTexte(ex)}>
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}

          {tours.map((tour, i) => (
            <div key={i} className={'tour ' + tour.role}>
              <p className="tour-texte">{tour.texte}</p>
              {tour.annuler && (
                <button
                  type="button"
                  className="tour-annuler"
                  onClick={() => annulerTour(i)}
                  disabled={tour.annule}
                >
                  {tour.annule ? 'Annulé' : 'Annuler'}
                </button>
              )}
            </div>
          ))}

          {/* Ce qu'il entend, pendant qu'on parle : la preuve que le micro vit. */}
          {partiel !== '' && (
            <div className="tour moi provisoire">
              <p className="tour-texte">{partiel}</p>
            </div>
          )}

          {occupe && (
            <div className="tour assistant provisoire" role="status">
              <p className="tour-texte">…</p>
            </div>
          )}

          <div ref={bas} />
        </div>

        {erreur && <p className="message-erreur">{erreur}</p>}

        {vocal ? (
          /* Le micro occupe le centre, en bas : c'est le seul objet qu'on
             regarde pendant qu'on parle, et le seul qu'on vise. L'etat se lit
             dessous, « Terminer » en retrait pour qu'on ne le prenne pas pour
             le bouton principal. */
          <div className="scene-vocale" role="status">
            <button
              type="button"
              className={'micro-gros' + (ecoute ? ' ecoute' : '') + (parle ? ' parle' : '')}
              // Evenements pointeur plutot que `onClick` : ils couvrent le
              // doigt comme la souris, et surtout ils distinguent l'appui du
              // relachement — ce qu'un clic, qui n'arrive qu'a la fin, ne
              // permet pas.
              onPointerDown={(e) => {
                // La capture garantit de recevoir le relachement meme si le
                // doigt a glisse hors du bouton en parlant. Sans elle, le tour
                // resterait ouvert et rien ne partirait.
                try {
                  e.currentTarget.setPointerCapture(e.pointerId);
                } catch {
                  // Certains navigateurs refusent la capture. On ecoute quand
                  // meme : mieux vaut un relachement parfois manque qu'un
                  // bouton qui ne reagit pas du tout.
                }
                commencerAParler();
              }}
              onPointerUp={finirDeParler}
              onPointerCancel={finirDeParler}
              // L'appui long ouvre sinon le menu « copier / partager » d'iOS
              // en plein milieu d'une phrase.
              onContextMenu={(e) => e.preventDefault()}
              disabled={occupe || parle}
              aria-label="Maintenir pour parler"
            >
              <span className="halo" aria-hidden="true" />
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="9" y="3" width="6" height="11" rx="3" />
                <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
              </svg>
            </button>
            <span className="etat-vocal">{etat}</span>
            <button type="button" className="terminer" onClick={arreterVocal}>
              Terminer
            </button>
          </div>
        ) : (
          <>
            <label className="champ">
              <span className="invisible">Dis-moi</span>
              <div className="champ-vocal">
                <textarea
                  value={texte}
                  onChange={(e) => setTexte(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void envoyer(texte, false);
                    }
                  }}
                  rows={2}
                  placeholder={
                    vocalPossible
                      ? 'Parle, ou écris ici…'
                      : 'Écris ici — ou utilise la touche micro du clavier.'
                  }
                  autoCapitalize="sentences"
                />
                {vocalPossible && (
                  <button type="button" className="micro" onClick={entrerEnVocal} aria-label="Parler">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <rect x="9" y="3" width="6" height="11" rx="3" />
                      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
                    </svg>
                  </button>
                )}
              </div>
            </label>

            <div className="feuille-actions">
              <button type="button" className="bouton discret" onClick={fermer}>
                Fermer
              </button>
              <button
                type="button"
                className="bouton primaire"
                onClick={() => void envoyer(texte, false)}
                disabled={occupe || texte.trim() === ''}
              >
                {occupe ? 'Un instant…' : 'Envoyer'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
